import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header';
import { styles, colors, radius } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const SiparisOlusturPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const transferredTalep = location.state?.transferredTalep || null;

  const [tedarikciler, setTedarikçiler] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [selectedTedarikci, setSelectedTedarikci] = useState('');
  const [kalemler, setKalemler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [stokBilgisi, setStokBilgisi] = useState(null);
  const [stokYükleniyor, setStokYükleniyor] = useState(false);

  // Manuel kalem form
  const [yeniKalem, setYeniKalem] = useState({
    malzeme_id: '',
    miktar: '',
    birim_fiyat: ''
  });

  useEffect(() => {
    // Tedarikcileri yukle
    fetch(`${API_URL}/api/tedarikciler`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => setTedarikçiler(Array.isArray(data) ? data : []))
      .catch(() => setTedarikçiler([]));

    // Malzemeleri yukle
    fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setMalzemeler(Array.isArray(data) ? data : []);

        // HANDLE TRANSFERRED TALEP
        if (transferredTalep && Array.isArray(data)) {
          const targetId = transferredTalep.malzeme_id || transferredTalep.malzeme;
          const matchedMalzeme = data.find(m => String(m.id) === String(targetId));

          if (matchedMalzeme) {
            const initialItem = {
              malzeme_id: matchedMalzeme.id,
              malzeme_ad: matchedMalzeme.ad,
              miktar: parseFloat(transferredTalep.miktar),
              birim: matchedMalzeme.birim,
              birim_fiyat: matchedMalzeme.fiyat || 0,
              talep_id: transferredTalep.id
            };
            setKalemler([initialItem]);
          } else if (transferredTalep.serbest_urun_ad) {
            // Serbest yazim talep - malzeme listesinde yok
            const initialItem = {
              malzeme_id: null,
              malzeme_ad: transferredTalep.serbest_urun_ad,
              miktar: parseFloat(transferredTalep.miktar),
              birim: transferredTalep.birim || 'kg',
              birim_fiyat: 0,
              talep_id: transferredTalep.id
            };
            setKalemler([initialItem]);
          }
        }
      })
      .catch(() => setMalzemeler([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stok bilgisini cek
  useEffect(() => {
    if (yeniKalem.malzeme_id) {
      setStokYükleniyor(true);
      fetch(`${API_URL}/api/stok/malzeme/${yeniKalem.malzeme_id}`, { headers: getAuthHeaders() })
        .then(res => res.json())
        .then(data => {
          setStokBilgisi(data);
          setStokYükleniyor(false);
        })
        .catch(() => {
          setStokBilgisi(null);
          setStokYükleniyor(false);
        });
    } else {
      setStokBilgisi(null);
    }
  }, [yeniKalem.malzeme_id]);

  const handleKalemEkle = () => {
    if (!yeniKalem.malzeme_id || !yeniKalem.miktar || !yeniKalem.birim_fiyat) {
      alert('Lütfen tum alanlari doldurun.');
      return;
    }

    const secilenMalzeme = malzemeler.find(m => m.id === parseInt(yeniKalem.malzeme_id));
    if (!secilenMalzeme) return;

    const yeniKalemData = {
      malzeme_id: parseInt(yeniKalem.malzeme_id),
      malzeme_ad: secilenMalzeme.ad,
      miktar: parseFloat(yeniKalem.miktar),
      birim: secilenMalzeme.birim,
      birim_fiyat: parseFloat(yeniKalem.birim_fiyat)
    };

    setKalemler([...kalemler, yeniKalemData]);
    setYeniKalem({ malzeme_id: '', miktar: '', birim_fiyat: '' });
  };

  const handleKalemSil = (index) => {
    setKalemler(kalemler.filter((_, i) => i !== index));
  };

  const handleBirimFiyatChange = (index, value) => {
    const newKalemler = [...kalemler];
    newKalemler[index].birim_fiyat = parseFloat(value) || 0;
    setKalemler(newKalemler);
  };

  // Hesaplamalar
  const araToplam = kalemler.reduce((sum, k) => sum + (k.miktar * k.birim_fiyat), 0);
  const kdv = araToplam * 0.08;
  const genelToplam = araToplam + kdv;

  const handleSubmit = async () => {
    if (!selectedTedarikci) {
      alert('Lütfen tedarikçi seçiniz.');
      return;
    }
    if (kalemler.length === 0) {
      alert('Lütfen en az bir kalem ekleyiniz.');
      return;
    }

    setLoading(true);

    const siparisData = {
      tedarikci_id: parseInt(selectedTedarikci),
      kalemler: kalemler.map(k => ({
        malzeme_id: k.malzeme_id,
        miktar: k.miktar,
        birim: k.birim,
        birim_fiyat: k.birim_fiyat,
        talep_id: k.talep_id || null,
      }))
      // oluşturan backend'de current_user'dan alınır (SA-02) — client göndermez
    };

    try {
      const response = await fetch(`${API_URL}/api/siparisler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(siparisData)
      });

      if (response.ok) {
        const result = await response.json();
        invalidateQueries(['siparis', 'dashboard', 'talep']);

        let successMessage = `Sipariş başarıyla oluşturuldu! Sipariş No: ${result.siparis_no}`;

        if (result.durum === 'ONAY_BEKLIYOR') {
          successMessage += '\n\nBu siparis Genel Mudur onayi bekliyor (>= 15.000 TL).';
        } else {
          successMessage += '\n\nSipariş BEKLIYOR durumunda. Satın alma sipariş verecek.';
        }

        setMessage(successMessage);

        setTimeout(() => {
          setSelectedTedarikci('');
          setKalemler([]);
          setMessage('');
          navigate('/siparisler');
        }, 3000);
      } else {
        const error = await response.json();
        alert(`Hata: ${error.detail}`);
      }
    } catch (err) {
      alert('Baglanti hatası!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header title="Yeni Sipariş Oluştur" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {message && (
          <div style={{ background: '#d4edda', padding: '15px', marginBottom: '20px', borderRadius: radius.sm, color: '#155724', fontWeight: 'bold', whiteSpace: 'pre-line' }}>
            {message}
          </div>
        )}

        {/* Transfer bilgisi */}
        {transferredTalep && (
          <div style={{ background: '#d1ecf1', padding: '12px 20px', marginBottom: '20px', borderRadius: radius.sm, color: '#0c5460', borderLeft: `4px solid ${styles.info}` }}>
            Talep No: <strong>{transferredTalep.talep_no}</strong> uzerinden siparis oluşturuluyor.
          </div>
        )}

        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* 1. Tedarikçi Seçimi */}
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>1. Tedarikçi Seçimi</h3>
            <select
              value={selectedTedarikci}
              onChange={(e) => setSelectedTedarikci(e.target.value)}
              style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: radius.sm, fontSize: '15px' }}
            >
              <option value="">Tedarikçi seçiniz...</option>
              {tedarikciler.map(t => (
                <option key={t.id} value={t.id}>{t.unvan}</option>
              ))}
            </select>
          </div>

          {/* 2. Manuel Kalem Ekleme */}
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>2. Sipariş Kalemleri</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '15px', alignItems: 'end', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Malzeme *</label>
                <select
                  value={yeniKalem.malzeme_id}
                  onChange={(e) => setYeniKalem({ ...yeniKalem, malzeme_id: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: radius.sm }}
                >
                  <option value="">Malzeme seçiniz...</option>
                  {malzemeler.map(m => (
                    <option key={m.id} value={m.id}>{m.ad} ({m.birim})</option>
                  ))}
                </select>
                {stokYükleniyor && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: colors.textSecondary }}>Stok kontrol ediliyor...</div>
                )}
                {stokBilgisi && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    borderRadius: radius.sm,
                    background: stokBilgisi.renk === 'green' ? '#d4edda' : stokBilgisi.renk === 'orange' ? '#fff3cd' : '#f8d7da',
                    border: `1px solid ${stokBilgisi.renk === 'green' ? '#c3e6cb' : stokBilgisi.renk === 'orange' ? '#ffeaa7' : '#f5c6cb'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', color: stokBilgisi.renk === 'green' ? '#155724' : stokBilgisi.renk === 'orange' ? '#856404' : '#721c24' }}>
                        Mevcut Stok: {stokBilgisi.toplam_stok} {stokBilgisi.birim}
                      </div>
                      {stokBilgisi.durum !== 'NORMAL' && (
                        <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>
                          Min. Stok: {stokBilgisi.min_stok} {stokBilgisi.birim} - {stokBilgisi.durum}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Miktar *</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Orn: 100"
                  value={yeniKalem.miktar}
                  onChange={(e) => setYeniKalem({ ...yeniKalem, miktar: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: radius.sm, boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>Birim Fiyat (TL) *</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Orn: 285.50"
                  value={yeniKalem.birim_fiyat}
                  onChange={(e) => setYeniKalem({ ...yeniKalem, birim_fiyat: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: radius.sm, boxSizing: 'border-box' }}
                />
              </div>

              <button
                onClick={handleKalemEkle}
                style={{ padding: '10px 20px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
              >
                + Ekle
              </button>
            </div>

            {/* Eklenen Kalemler Tablosu */}
            {kalemler.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: styles.secondary }}>Eklenen Kalemler:</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ background: styles.primary, color: 'white' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Malzeme</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Miktar</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Birim Fiyat</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Tutar</th>
                      <th style={{ padding: '8px', textAlign: 'center', width: '80px' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kalemler.map((kalem, index) => {
                      const tutar = kalem.miktar * kalem.birim_fiyat;
                      return (
                        <tr key={index} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ padding: '12px' }}>
                            {kalem.malzeme_ad}
                            {kalem.talep_id && (
                              <span style={{ fontSize: '10px', color: colors.textTertiary, display: 'block' }}>(talepten aktarildi)</span>
                            )}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{kalem.miktar} {kalem.birim}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={kalem.birim_fiyat}
                              onChange={(e) => handleBirimFiyatChange(index, e.target.value)}
                              style={{ width: '80px', padding: '5px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, textAlign: 'right' }}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                            {tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <button
                              onClick={() => handleKalemSil(index)}
                              style={{ padding: '5px 10px', background: styles.danger, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}
                            >
                              Sil
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Toplam Hesaplamalar */}
                <div style={{ borderTop: '2px solid #dee2e6', paddingTop: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                    <span style={{ minWidth: '150px', textAlign: 'right', marginRight: '20px' }}>Ara Toplam:</span>
                    <strong style={{ minWidth: '120px', textAlign: 'right' }}>{araToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                    <span style={{ minWidth: '150px', textAlign: 'right', marginRight: '20px' }}>KDV (%8):</span>
                    <strong style={{ minWidth: '120px', textAlign: 'right' }}>{kdv.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px 0', borderTop: '2px solid #dee2e6' }}>
                    <span style={{ minWidth: '150px', textAlign: 'right', marginRight: '20px', fontSize: '18px' }}>Genel Toplam:</span>
                    <strong style={{ minWidth: '120px', textAlign: 'right', fontSize: '20px', color: styles.primary }}>
                      {genelToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                    </strong>
                  </div>
                </div>

                {/* Onay Uyarisi - Yeni esikler */}
                {(() => {
                  if (genelToplam >= 15000) {
                    return (
                      <div style={{
                        background: '#fff3cd',
                        border: '2px solid #ffc107',
                        borderRadius: radius.md,
                        padding: '15px',
                        marginTop: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '24px' }}>&#x1F454;</span>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#856404', marginBottom: '5px' }}>
                            Genel Mudur Onayi Gerekli
                          </div>
                          <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                            Bu siparis 15.000 TL uzerinde oldugu icin Genel Mudur onayi bekleyecektir.
                            Onay sonrasi satın alma siparis verecek.
                          </div>
                        </div>
                      </div>
                    );
                  } else if (kalemler.length > 0) {
                    return (
                      <div style={{
                        background: '#d1ecf1',
                        border: '2px solid #17a2b8',
                        borderRadius: radius.md,
                        padding: '15px',
                        marginTop: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '24px' }}>&#x1F4E6;</span>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#0c5460', marginBottom: '5px' }}>
                            Satin Alma Islemi Bekleyecek
                          </div>
                          <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                            Bu siparis 15.000 TL altinda. Oluşturulduktan sonra satın alma biriminin
                            siparis vermesi beklenecektir.
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Sipariş Oluştur Butonu */}
                <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !selectedTedarikci}
                    style={{
                      flex: 1,
                      padding: '15px',
                      background: loading || !selectedTedarikci ? '#ccc' : styles.success,
                      color: 'white',
                      border: 'none',
                      borderRadius: radius.sm,
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: loading || !selectedTedarikci ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Isleniyor...' : 'SIPARISI OLUSTUR'}
                  </button>
                  <button
                    onClick={() => navigate('/siparisler')}
                    style={{
                      padding: '15px 30px',
                      background: '#e0e0e0',
                      color: styles.secondary,
                      border: 'none',
                      borderRadius: radius.sm,
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    IPTAL
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiparisOlusturPage;
